import { useMemo } from "react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
  Calculator,
  Lock,
  Unlock,
  Eye,
  Trash2,
  Calendar,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Plus,
} from "lucide-react";
import LoadingIndicator from "@/components/LoadingIndicator";
import { useMonthEndStock } from "@/hooks/useMonthEndStock";
import { useToast } from "@/hooks/use-toast";
import type { MonthEndStock } from "@/types/monthEndStock";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLocation } from "wouter";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

export const MonthEndStockList = () => {
  const { format: formatDecimal } = useDecimalPrecision();
  const { toast } = useToast();
  const [_, navigate] = useLocation();
  const {
    periods,
    isLoadingPeriods,
    periodsError,
    unlockPeriod,
    isUnlocking,
    deletePeriod,
    isDeleting,
  } = useMonthEndStock();

  // Summary statistics
  const summary = useMemo(() => {
    if (!periods || periods.length === 0) {
      return {
        totalPeriods: 0,
        lockedPeriods: 0,
        latestPeriod: null,
      };
    }

    return {
      totalPeriods: periods.length,
      lockedPeriods: periods.filter((p) => p.locked).length,
      latestPeriod: periods[0], 
    };
  }, [periods]);

  const handleUnlockPeriod = async (periodId: string) => {
    try {
      await unlockPeriod({ periodId });
      toast({
        title: "Period Unlocked",
        description: "The period has been unlocked",
        duration: 5000,
        icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
      });
    } catch (error: any) {
      toast({
        title: "Unlock Failed",
        description: error.response?.data?.message || error.message || "Failed to unlock period",
        variant: "destructive",
      });
    }
  };

  const handleDeletePeriod = async (periodId: string) => {
    try {
      await deletePeriod(periodId);
      toast({
        title: "Period Deleted",
        description: "The period has been deleted",
        duration: 5000,
        icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
      });
    } catch (error: any) {
      toast({
        title: "Delete Failed",
        description: error.response?.data?.message || error.message || "Failed to delete period",
        variant: "destructive",
      });
    }
  };

  const handleViewDetails = (period: MonthEndStock) => {
    navigate(`/month-end-stock/view/${period.id}`);
  };

  if (isLoadingPeriods) return <LoadingIndicator />;

  if (periodsError) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-950/20 dark:border-red-900/50 dark:text-red-400 px-4 py-3 rounded">
          <p className="font-semibold mb-2">Error Loading Month End Stock</p>
          <p className="text-sm">{periodsError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Month End Stock</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Batch recalculation and period locking for weighted average
          </p>
        </div>
        <Button onClick={() => navigate("/month-end-stock/add")}>
          <Plus className="h-4 w-4 mr-2" />
          New Recalculation
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Periods</p>
                <h3 className="text-2xl font-bold mt-2">{summary.totalPeriods}</h3>
              </div>
              <Calendar className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Locked Periods</p>
                <h3 className="text-2xl font-bold mt-2">{summary.lockedPeriods}</h3>
              </div>
              <Lock className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Latest Period</p>
                <h3 className="text-lg font-bold mt-2">
                  {summary.latestPeriod ? `Period ${summary.latestPeriod.periodNumber}` : "N/A"}
                </h3>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Periods List */}
      <Card>
        <CardHeader>
          <CardTitle>Month End Stock Periods</CardTitle>
          <CardDescription>View and manage month-end stock recalculation periods</CardDescription>
        </CardHeader>
        <CardContent>
          {periods && periods.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Voucher Number</TableHead>
                  <TableHead>Date Range</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Value Change</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {periods.map((period) => (
                  <TableRow key={period.id}>
                    <TableCell className="font-medium">Period {period.periodNumber}</TableCell>
                    <TableCell>{period.voucherNumber}</TableCell>
                    <TableCell>
                      {format(new Date(period.startDate), "dd/MM/yyyy")} -{" "}
                      {format(new Date(period.endDate), "dd/MM/yyyy")}
                    </TableCell>
                    <TableCell>{period.totalItems}</TableCell>
                    <TableCell className={period.totalValueChange && period.totalValueChange >= 0 ? "text-green-600" : "text-red-600"}>
                      RM {formatDecimal(Math.abs(period.totalValueChange || 0))}
                      {period.totalValueChange && period.totalValueChange >= 0 ? (
                        <TrendingUp className="inline h-4 w-4 ml-1" />
                      ) : (
                        <TrendingDown className="inline h-4 w-4 ml-1" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant={period.locked ? "default" : "secondary"}>
                          {period.locked ? (
                            <>
                              <Lock className="h-3 w-3 mr-1" />
                              Locked
                            </>
                          ) : (
                            <>
                              <Unlock className="h-3 w-3 mr-1" />
                              Unlocked
                            </>
                          )}
                        </Badge>
                        {period.hasVariances && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <AlertCircle className="h-4 w-4 text-yellow-600" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Contains variances</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(period)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {period.locked ? (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" disabled={isUnlocking}>
                                <Unlock className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Unlock Period {period.periodNumber}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will unlock the period and allow edits to transactions in this date range.
                                  Note: You can only unlock the most recent locked period.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleUnlockPeriod(period.id)}>
                                  Unlock
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        ) : (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" disabled={isDeleting}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Period {period.periodNumber}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This action cannot be undone. This will permanently delete the period
                                  and all associated data.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeletePeriod(period.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Calculator className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-lg font-medium">No month-end stock periods</p>
              <p className="text-sm mt-2">Create your first recalculation period to get started</p>
              <Button
                onClick={() => navigate("/month-end-stock/add")}
                className="mt-4"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Period
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MonthEndStockList;
